/**
 * `EX-IDN03` (ADR-0088 decision 7): the countries a person can pick, as
 * ISO 3166-1 alpha-2 codes (the 249 assigned codes plus XK, Kosovo, which the
 * ISO list leaves out and people still live in). The API accepts the same
 * set (`progsity-backend` `country-codes.ts`, the two apps share no code).
 *
 * Names come from `Intl.DisplayNames` in the reader's language, so there is
 * no table of names to keep: "Bangladesh" on /en, "বাংলাদেশ" on /bn.
 */
export const COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS",
  "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
  "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE",
  "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
  "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM",
  "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC",
  "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
  "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
  "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG",
  "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS",
  "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO",
  "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "XK", "YE", "YT", "ZA", "ZM", "ZW",
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

const CODE_SET: ReadonlySet<string> = new Set(COUNTRY_CODES);

export function isCountryCode(value: unknown): value is CountryCode {
  return typeof value === "string" && CODE_SET.has(value);
}

const displayNames = new Map<string, Intl.DisplayNames>();

/** The country's name in the reader's language ("Bangladesh", "বাংলাদেশ"). */
export function countryName(code: string, locale: string): string {
  const tag = locale === "bn" ? "bn" : "en";
  let names = displayNames.get(tag);
  if (!names) {
    names = new Intl.DisplayNames([tag], { type: "region" });
    displayNames.set(tag, names);
  }
  return names.of(code) ?? code;
}

/** The bundled flag (`public/flags`, copied from `flag-icons` before every build). */
export function flagSrc(code: string): string {
  return `/flags/${code.toLowerCase()}.svg`;
}

/**
 * The country the reader's device is set to, from its time zone, the way the
 * contest pages choose Bangladesh time (`features/contests/lib/display-zone.ts`).
 * Only Bangladesh is guessed: a wrong guess anywhere else is worse than none.
 */
export function guessCountryFromTimeZone(): CountryCode | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone === "Asia/Dhaka" || zone === "Asia/Dacca" ? "BD" : null;
  } catch {
    return null;
  }
}
